import React, { useEffect, useState } from 'react';
import { useAuth } from './Auth';
import { useNavigate } from 'react-router-dom';
import { ImageItem } from './types';
import { RowsPhotoAlbum } from 'react-photo-album';
import 'react-photo-album/rows.css';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import { api } from './utils/api';

const ProjectView: React.FC = () => {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [images, setImages] = useState<ImageItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [openProject, setOpenProject] = useState<string | null>(null);
    const [lightboxIndex, setLightboxIndex] = useState(-1);
    const [lightboxProject, setLightboxProject] = useState<string | null>(null);

    useEffect(() => {
        console.log("User looking for projects", user)
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        const load = async () => {
            console.log('loading images for user', user);
            setLoading(true);
            try {
                const client = encodeURIComponent(user?.ID || '');
                const res = await api.get(`/getImagesByClient?client=${client}`);
                if (!res.ok) {
                    console.error('failed to load images by client', res.status);
                    setImages([]);
                } else {
                    const data = await res.json() as ImageItem[];
                    setImages(data || []);
                }
            } catch (err) {
                console.error('error loading images', err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [isAuthenticated, user, navigate]);

    // group images by project
    const projects = images.reduce((acc: Record<string, ImageItem[]>, img) => {
        const p = img.project || 'Uncategorized';
        if (!acc[p]) acc[p] = [];
        acc[p].push(img);
        return acc;
    }, {});

    const handlePhotoClick = (project: string, index: number) => {
        setLightboxProject(project);
        setLightboxIndex(index);
    };

    const downloadProjectZip = async (projectName: string) => {
        if (!user) return;
        try {
            setLoading(true);
            const client = encodeURIComponent(user?.ID || '');
            const res = await api.get(`/downloadProject?client=${client}&project=${encodeURIComponent(projectName)}`);
            if (!res.ok) {
                console.error('failed to download project zip', res.status);
                return;
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${projectName}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('error downloading project zip', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: '2rem' }}>
            <h1>My Projects</h1>
            {!isAuthenticated && <p>Please log in to view your projects.</p>}
            {loading && <p>Loading...</p>}
            {!loading && Object.keys(projects).length === 0 && <p>No projects found for your account.</p>}
            {Object.entries(projects).map(([projectName, imgs]) => (
                <section key={projectName} style={{ marginBottom: '1rem', border: '1px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
                    <details open={openProject === projectName} onToggle={(e) => setOpenProject((e.target as HTMLDetailsElement).open ? projectName : null)}>
                        <summary style={{ padding: '0.75rem 1rem', background: '#fafafa', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span>{projectName} ({imgs.length})</span>
                            <button type="button" onClick={(e) => { e.stopPropagation(); downloadProjectZip(projectName); }} style={{ marginLeft: 12, padding: '0.25rem 0.5rem', fontSize: '0.9rem' }}>Download All</button>
                        </summary>
                        <div style={{ padding: '1rem' }}>
                            <RowsPhotoAlbum
                                photos={imgs.map(img => ({ src: img.thumbnailUrl, width: img.width || 200, height: img.height || 150 }))}
                                targetRowHeight={200}
                                onClick={({ index }) => handlePhotoClick(projectName, index)}
                            />
                        </div>
                    </details>
                </section>
            ))}

            {lightboxProject && lightboxIndex >= 0 && (
                <Lightbox
                    open={true}
                    close={() => { setLightboxIndex(-1); setLightboxProject(null); }}
                    slides={(projects[lightboxProject] || []).map(img => ({ src: img.fullImageUrl, alt: img.fileName }))}
                    index={lightboxIndex}
                    plugins={[Zoom]}
                />
            )}
        </div>
    );
};

export default ProjectView;
